// src/lib/translationsLandingB.ts

const en = {
  landingHeroHeadline: "Spin up your revenue<br/>– by up to 50%! 🚀",
  landingHeroSubline: `Put a QR-powered <strong>digital Prize Wheel</strong> into your guests' hands and pocket these three benefits:<br/><br/>
🎯 <strong>more repeat guests</strong>,<br/>
⭐ <strong>more Google reviews</strong>,<br/>
📩 <strong>a growing email list</strong>.<br/><br/>
These are the building blocks of a fully booked business – all with just one subscription.`,
  landingHeroCta: "This is exactly what I need",

  postHeroSeparatorWheel: "One prize wheel — triple win: grow your footfall, revenue, and reputation.",

  reliefBlock: {
    title: "Hospitality can be easier",
    p1: "That knot in your stomach after a weak month? The <strong>constant uncertainty, empty tables and roller-coaster revenue</strong> haunt almost every owner. Sometimes you know why — seasonality, no events — other times you just stare, wondering why it's quiet while competitors are full and glowing with reviews.",
    p2: "Your <strong>place is better</strong>, fewer people have just said it out loud. You can't raise prices further; costs eat your margin. The maths is simple: <strong>you need more guests</strong>.",
    p3: "Enter the <strong>Review-to-Revenue digital prize wheel</strong>. One QR code, and your guests happily help you collect more reviews, bring back repeat visits, and grow your email list.",
    p4: "Next month you'll open your numbers and <strong>feel calm instead of clutching your stomach</strong> — because you finally have a system that makes demand steadier and more predictable.",
    p5: "Imagine your venue becoming the neighbourhood's <strong>\"legendary\" hangout</strong> — the local Central Perk or MacLaren's. Regulars return like family, first-timers sit with confidence. You worry less about empty tables and more about when the queue will fit inside.<br/><br/>And it takes <strong>no magic — just a simple, smart tool</strong> that puts you on a steady growth path and lets you finally breathe.",   
    cta: "LET'S START NOW",
  },

  calculatorIntro: {
    text: 'Imagine you had a <strong>special bank account</strong>. Its balance shows the amount <strong>you could have earned in a given month</strong> — but didn\'t, because you kept everything as it is. <br/><br/>How big is that amount? <strong>You can easily calculate it.</strong><br/><br/>With this calculator you\'ll see <strong>how much money you miss out on every month</strong> if you don\'t set up a <strong>customer return, Google Review, and email collection system</strong>.'
  },
  revenueCouldHaveMade: "Revenue you miss out on every month",

  postHeroMistake: {
    title: "Most owners make this expensive mistake",
    intro: "Most hospitality owners pour <strong>the lion's share of their marketing budget into chasing new guests</strong>. You've heard it before, right?",
    channels: {
      title: "Research says:",
      bullets: [
        "polished TikTok videos,",
        "influencer collabs,",
        "Google & Facebook ads,",
        "or fully outsourced marketing."
      ]
    },
    costQuestion: "They can bring some traffic — but the <strong>real question: is it worth the massive monthly burn?</strong>",
    pivot: "Meanwhile there's a far more profitable group: your existing guests.",
    researchTitle: "Research says:",
    researchBullets: [
      `acquiring a new customer costs 5–10× more than retaining one`,
      `repeat customers spend ~67% more on average`,
      `a +5% retention lift can grow profit by 25–95%`
    ],
    conclusion: "In short: <strong>you're better off not chasing only new people, but strengthening your regulars.</strong>"
  },

  newGuestsBlock: {
    title: "So… should I forget new guests?",
    intro: "Not at all. You still need demand — just acquire smarter. <strong>Most first-timers decide based on Google reviews</strong> when choosing between places.",
    strategy: "If your star average is strong, that's not luck — it's a system. Review-to-Revenue helps with that <strong>too</strong>: it automates the process and multiplies results.",
    warning: "If you're at 4.4★ or below, maths is brutal: a 4.8★ venue wins every time. That can mean <strong>3–5 lost guests a day — ~€2–3k/month gone</strong>.",
    question: "Can you afford that?",
    solution: "Review-to-Revenue fixes this problem by making it fun and rewarding for guests to leave reviews.",
    cta: "SHOW ME THE SOLUTION"
  },

  crisisHeadline: "People choose by Google reviews — especially tourists and first-timers.",
  crisisDescription: `If you're already great at earning stars, it's not luck — it's strategy. You'll <span class="font-semibold text-[#4FC3F7]">love</span> Review-to-Revenue because it <span class="font-semibold text-[#4FC3F7]">automates</span> your flow and <span class="font-semibold text-[#4FC3F7]">multiplies</span> outcomes.<br/><br/>
If you're at 4.5★ or below, face the hard truth: with a 4.8★ competitor around the corner, <span class="font-semibold">4.8 beats 4.5 every single time.</span> That's daily lost covers — real money.`,
  crisisSolution: `We've built a <span class="font-semibold text-[#4FC3F7]">ridiculously simple system</span> that wins this back for the price of two pizzas.`,

  separatorWheel: "One prize wheel — triple upside: more guests, more revenue, stronger reputation.",

  setupStepsTitle: "Create your revenue-boosting system in just 15 minutes",
  setupStepsSubtitle: "The guest has paid, they're smiling, phone in hand — this is the perfect 'wow moment'. With the QR prize wheel, in one move you can:",
  setupStepsHighlights: [
    "🎯 encourage them to come back,",
    "⭐ ask for a five-star review,",
    "📩 and collect their email."
  ],
  setupStepsIntro: "",
  setupSteps: [
    "Click the Register button and create your business profile (3 min).",
    "Add prizes and their odds to your prize wheel (2 min).",
    "Download your unique QR code (1 min).",
    "Use it digitally or print it on stickers, flyers, or table tents (5 min).",
    "Place it in visible spots: on tables, at checkout, even in the restroom (4 min)."
  ],
  setupStepsTip: "💡 <strong>Tip: Next to the QR code, display the top prize and the line \"Spin to win a prize!\"</strong> — it instantly grabs attention.\n\nMake the top prize valuable (e.g. dinner for two, a bottle of wine) but set the chance low (1–2%). Let common prizes be small but joyful (e.g. free coffee, 5% discount, 1+1 offer).",
  setupStepsOutro: "After that, the system automatically brings you repeat customers, five-star reviews, and email subscribers — month after month.",
  setupStepsFinalQuestion: "Want to spin up your revenue too?",
  setupStepsCta: "YES, LET'S GO!",
  setupStepsImageAlt: "barista photo",

  r2rHowToUseTitle: "How guests experience Review-to-Revenue",
  r2rHowToUseLead: "Your guest just had a great time — <strong>perfect moment to ask for feedback</strong>. If they can also win something… even better! 🎉",
  r2rHowToUseStepsTitle: "Step by step:",
  r2rHowToUseSteps: [
    "<strong>Ask them to spin for a prize</strong>.",
    "They spin → <strong>win a small treat/discount</strong> that brings them back.",
    "The system asks for a Google review — and they're <strong>more willing to leave it</strong> right after the win.",
    "<strong>They share their email</strong> — the timing is perfect.",
    "You've just <strong>created a new regular, earned a 5-star review and captured an email</strong>."
  ],
  r2rHowToUseFinalQuestion: "👉 Ready to boost your floor traffic?",
  r2rHowToUseCta: "START NOW",

  gamifiedRequestHeadline: "This is why the Review-to-Revenue method works",
  gamifiedRequestText: "🔁 <strong>Drives repeat visits</strong> - Guests win a prize after every spin, which they can redeem at your place with a <strong>one-time QR code</strong>. They also get an <strong>automatic reminder</strong> to redeem it — complete with your <strong>table booking link</strong>.<br/><br/>" +
    "🎯 <strong>Reviews made fun</strong> – Instead of a boring \"please review us,\" guests play a game. The <strong>prize makes them feel grateful</strong>, so they're <strong>far more likely to leave a positive Google Review</strong>.<br/><br/>" +
    "📱 <strong>Just 30 seconds</strong> – <strong>No app, no signup</strong>: <strong>scan the QR, spin, enter email, leave a review</strong>. Perfectly fits the <strong>Gen-Y and Gen-Z mobile-first lifestyle</strong>.",
  gamifiedRequestCta: "I'M IN",
  r2rGuestExperienceHeadline: "How guests experience Review-to-Revenue",
  engagementBoostHeadline: "",
  ctaSecondary: "Let's do it",
  ctaTertiary: "Try it",

  trustedBySectionHeadline: "It's for you if…",
  trustedByCards: [
    "<strong>Your regulars faded, new guests are slow</strong><br/>You need a system that brings people back — while still attracting new ones.",
    "<strong>You just opened and are invisible on Google Maps</strong><br/>Without ~100 reviews tourists simply pass by. This wheel gets you there fast.",
    "<strong>A \"TikTok-star\" competitor opened nearby</strong><br/>If you don't lock in your guests, hype will steal them over time.",
    "<strong>Your rating slipped below 4.5★</strong><br/>Continuous positives help recover the average — and new demand.",
    "<strong>No more ad budget left</strong><br/>Build an owned, low-cost channel that works every day."
  ],
  trustedBySectionClosing: "",
  trustedByCta: "THAT'S ME — LET'S GO",
  partnersImageAlt: "Partners photo",

  notForYouBlock: {
    title: "It's not for you if…",
    bullets: [
      {
        title: "You don't mind expensive ads",
        text: "You're more comfortable paying Meta or Google thousands every month instead of building your own repeat audience for pennies."
      },
      {
        title: "You won't put yourself into it",
        text: "You think buying a tool means you can sit back — but any system only works if you actively use it."
      },
      {
        title: "You hate giving small perks",
        text: "You refuse to offer a free coffee or 5% off for loyal guests, but have no problem burning money on design, decor or PR."
      },
      {
        title: "You ignore your competitors",
        text: "It doesn't bother you that they're full of happy guests and 5-star reviews while your till goes quiet."
      },
      {
        title: "You don't care about bad reviews",
        text: "You accept that a few negative ratings drag down your Google average — and cost you bookings."
      }
    ],
    conclusion1: "Then we're probably not a match.",
    conclusion2: "Review-to-Revenue is for owners who won't settle for average — but keep improving.",
    conclusion3: "Our 3-in-1 prize wheel multiplies their work and results.",
    cta: ""
  },

  coreBenefits: {
    skyHighStarRating: "Sky-high star rating",
    captureAuthenticFeedback: "Capture authentic feedback at peak satisfaction",
    privateNegativeFeedback: "Email capture",
    addressConcerns: "After the win & review, email is a natural next step for offers.",
    moreRepeatVisits: "More repeat visits",
    turnOneTimeVisitors: "Turn one-timers into loyal regulars",
    moreRepeatGuestsTitle: "+21% more repeat guests",
    moreRepeatGuestsDesc: "that stabilizes your revenue",
    moreReviewsTitle: "5× more 5-star reviews",
    moreReviewsDesc: "that attract new guests",
    moreEmailSubsTitle: "32% email signup rate",
    moreEmailSubsDesc: "that builds your own sales channel"
  },

  revenueBoostHeadline: "Review-to-Revenue spins profits for:",
  restaurantsTitle: "Restaurants",
  barbershopsTitle: "Barbershops",
  cafesTitle: "Cafés",
  hotelsTitle: "Hotels",
  retailTitle: "Retail",
  salonsTitle: "Salons",
  trustedByMore: "Gyms, bistros, bakeries, taprooms, breakfast bars, salad shops, florists, mechanics, massage, and more.",

  satisfiedGuestsToMarketing: "One wheel, triple win: more guests, higher revenue, stronger reputation.",
  statsTitle: "+15% reviews / +21% repeats / +32% opt-ins",
  statsDescription: "Across pilots in 3 months: ~5× more fresh reviews, +21% repeat visits, and ~1/3 of spins become email opt-ins.",

  testimonialsTitle: "What customers say",
  testimonialsRepeat: {
    review1: "From first-timer to regular: +31% repeat visits in 6 weeks — with zero extra ads.",
    review2: "Weekend re-bookings +27%. The email list from the wheel pays for itself every Friday.",
    review3: "Locals keep coming back for small rewards. +18% monthly revenue without Facebook ads.",
    name1: "Koloni Restaurant",
    name2: "Bali Babe Salon",
    name3: "YOU Suite Seminyak"
  },
  finalCtaTitle: "Finally put your venue on a stable growth path!",
  finalCtaDescription: "Start collecting 5-star reviews within minutes with a game your guests will love.",

  moreReviewsFlow: "More returning guests ➕ more Google Reviews ➕ more email subscribers = Revenue boost 🚀",
  pricingTeaserTitle: "More revenue for the price of a cheap dinner for 2.",

  ltdTeaser: {
    title: "Lifetime Offer - limited time!",
    intro: "Get <strong>lifetime access</strong> to Review-to-Revenue's digital Wheel of Fortune for the price of just <strong>a few months of subscription</strong>! <br/><br/>Hurry up: <strong>prices increase month by month</strong>, and this option will end by <strong>January 1, 2026</strong> - or once we reach <strong>500 customers</strong>.",
    tiersTitle: "Price tiers",
    tiers: [
      "Super early bird: <strong>39,990 HUF</strong> until September 30",
      "Early bird: <strong>49,990 HUF</strong> until October 31",
      "Standard: <strong>59,990 HUF</strong> until November 30",
      "Last minute: <strong>69,990 HUF</strong> until December 31"
    ],
    closer: "Grab the Lifetime Deal now and save hundreds of thousands!",
    cta: "View Lifetime Deal",
    badge: "Lifetime - limited",
    note: "Valid until the deadlines above or while supplies last."
  },

  freeTrialBanner: "30 days money-back guarantee.",
  monthly: "Monthly",
  yearly: "Yearly",
  save20Percent: "Save 20%",

  pricingPlans: {
    starter: {
      name: "Starter",
      period: "/mo",
      buttonText: "Get started",
      features: [
        "Manage 1 location",
        "Up to 3 prize wheels",
        "Up to 200 new reviews/month",
        "Downloadable guest email list",
        "Short video tutorials",
        "Support within 24h",
        "Your own stats dashboard"
      ]
    },
    growth: {
      name: "Growth",
      period: "/mo",
      buttonText: "Choose plan",
      features: [
        "Everything in Starter",
        "Up to 5 locations",
        "Up to 15 wheels",
        "Up to 1000 new reviews/month",
        "Custom branding"
      ],
      badge: "Most popular"
    }
  },

  emailListBlock: {
    title: "The secret to staying fully booked",
    leadQuestion: "Do you know the cheapest and strongest sales channel?",
    leadAnswer: "👉 Your own email list.",
    reason1: "Sending an email <strong>costs just cents while landing directly in your guests' inbox</strong>. No algorithms decide whether they see it.",
    reasonCardText: "A well-timed newsletter (e.g. \"Friday night wine dinner\" or \"new seasonal pizza\") instantly brings them back — guests who otherwise would have gone elsewhere.",
    ownership: "And <strong>the list is yours</strong>. Not Google's, not Facebook's. Even if platforms change the rules, it still belongs to you.",
    challenge: "The only challenge: getting your guests to actually share their email.",
    r2rHelp: "📧 Here's how the Review-to-Revenue digital wheel of fortune helps: <strong>our clients achieve an average 32% opt-in rate!</strong>",
    speed: "That way your list grows at record speed — from your most valuable guests.",
    cta: "SHOW ME THE SOLUTION"
  },

  ltd: {
    badge: "Lifetime",
    title: "Lifetime Deal",
    subtitle: "Pay once. Use forever.",
    priceNote: "one-time for 1 location",
    features: {
      wheels: "Up to 3 prize wheels",
      reviews: "200 new reviews/month",
      emails: "Download guest emails",
      tutorials: "Short video tutorials",
      stats: "Your own stats dashboard",
      updates: "All future updates"
    },
    addExtraLabel: "Add extra location",
    each: "+{price} each",
    totalLabel: "Total",
    cta: "Get Lifetime Deal",
    redirecting: "Redirecting…"
  },

  qrScanImageAlt: "QR code scan"
};

export function getTranslationLandingB(_: string = 'en') {
  return en;
}
